export type JsonBranch = Record<string, unknown> | unknown[];

export const isBranch = (v: unknown): v is JsonBranch => typeof v === "object" && v !== null;

/** Null, undefined, "" and empty objects or arrays: shown dimmed. */
export function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined || v === "") return true;
  return isBranch(v) && Object.keys(v).length === 0;
}

/** Empty, or an object or array holding only blank values: hidden by "Hide empty". */
export function isBlank(v: unknown): boolean {
  return isEmpty(v) || (isBranch(v) && Object.values(v).every(isBlank));
}

/** Text that holds a JSON object or array, as that value; anything else as is. */
export function asJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!/^[[{]/.test(text)) return value;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return value;
  }
}

/** Pretty-printed JSON; a string stays the text it is. */
export function rawJson(value: unknown): string {
  if (value === undefined) return "";
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

/** One path segment appended to a parent path: `a.b`, or `a[0]` for an array index. */
export function childPath(parent: string, key: string, inArray: boolean): string {
  if (inArray) return `${parent}[${key}]`;
  return parent ? `${parent}.${key}` : key;
}

const INDEX = /^(?:\[(\d+)\]|(\d+)(?=$|[.[]))\.?/;

/**
 * The value at a dotted path (`input.report-text`), stepping into lists by
 * index (`messages[0].content` or `messages.0.content`). A key that itself
 * holds a dot is matched whole first, as the server reads paths. `undefined`: none.
 */
export function valueAt(doc: unknown, path: string): unknown {
  if (!path) return doc;
  if (!isBranch(doc)) return undefined;
  if (Array.isArray(doc)) {
    const step = INDEX.exec(path);
    return step ? valueAt(doc[Number(step[1] ?? step[2])], path.slice(step[0].length)) : undefined;
  }
  if (path in doc) return doc[path];
  for (const key of Object.keys(doc)) {
    const rest = path.startsWith(`${key}.`)
      ? path.slice(key.length + 1)
      : path.startsWith(`${key}[`)
        ? path.slice(key.length)
        : null;
    if (rest === null) continue;
    const found = valueAt(doc[key], rest);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** One line of a value for a hint: whitespace collapsed, cut with an ellipsis. */
export function oneLine(value: unknown, max = 40): string {
  if (value === undefined) return "";
  const text = (typeof value === "string" ? value : JSON.stringify(value)).replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
